import React, { useState } from 'react';
import { connect } from 'react-redux';
import Downshift from 'downshift';
import { useLocale } from 'locale';
import { ShowAt } from 'consts/breakpoints';
import Modal from 'ds/components/modal';
import ProfileIcon from 'assets/svg/investors-header-user.svg';
import DocumentIcon from 'assets/svg/investors-header-notebook.svg';
import BookmarkIcon from 'assets/svg/bookmark.svg';
import ExitIcon from 'assets/svg/logout_v2.svg';
import KebabDotsIcon from 'assets/svg/menu-dots.svg';
import UserIcon from 'assets/svg/investors-mobile-header-user.svg';
import CloseIcon from 'assets/svg/investors-header-close.svg';
import { userProfileFieldsSelector, IUserProfileFields } from 'components/navigation/profileFields';
import { navigateTo } from 'store/state/router/actions';
import { logout } from 'store/state/app/actions';
import { NavigateToFn, Route } from 'config/routes';
import { ProfileTab } from 'components/profile/Profile';
import { State } from 'store/state';
import {
  AvatarChip, DesktopDropdown, Initials, KebabIcon, MenuContainer, MenuIcon, MenuRow,
  MobileMenuButton, MobileMenuCloseButton, MobileMenuHeader, MobileMenuList, MobileMenuRow,
} from './AvatarMenu.styled';

interface AvatarMenuProps {
  userProfile: IUserProfileFields;
  navigate: NavigateToFn;
  onLogout: () => void;
}

const AvatarMenuBase: React.FC<AvatarMenuProps> = ({ userProfile, navigate, onLogout }) => {
  const { t } = useLocale();
  const [ isOpen, setIsOpen ] = useState(false);

  if (!userProfile || !userProfile.initials) return null;

  const goTo = (route: Route, params: Record<string, unknown> = {}) => () => {
    setIsOpen(false);
    navigate(route, params);
  };

  const handleLogout = () => {
    setIsOpen(false);
    onLogout();
  };

  return (
    <MenuContainer>
      {/* breakpoint split matches every other v2 section in this project (useScreenBreakpoint() <
          3 = mobile), not the old UserMenu's own at={1}/from={2} split, so this stays visually
          consistent with the rest of the new header at tablet width */}
      <ShowAt from={3}>
        <Downshift isOpen={isOpen} onOuterClick={() => setIsOpen(false)}>
          {() => (
            <div>
              <AvatarChip data-auto="investors_home_v2_header_avatar_button" onClick={() => setIsOpen(!isOpen)}>
                <KebabIcon><KebabDotsIcon /></KebabIcon>
                <Initials>{userProfile.initials}</Initials>
              </AvatarChip>
              {isOpen ? (
                <DesktopDropdown data-auto="investors_home_v2_header_avatar_menu">
                  <MenuRow data-auto="investors_home_v2_header_menu_my_account" onClick={goTo(Route.InvestorsProfilePage)}>
                    <MenuIcon iconWidth={14}><ProfileIcon /></MenuIcon>
                    {t('investors.home.v2.header.menu.myAccount')}
                  </MenuRow>
                  <MenuRow
                    data-auto="investors_home_v2_header_menu_investment_profile"
                    onClick={goTo(Route.InvestorsProfilePage, { initialTab: ProfileTab.Preferences })}
                  >
                    <MenuIcon iconWidth={16}><DocumentIcon /></MenuIcon>
                    {t('investors.home.v2.header.menu.investmentProfile')}
                  </MenuRow>
                  <MenuRow data-auto="investors_home_v2_header_menu_logout" onClick={handleLogout}>
                    <MenuIcon iconWidth={20}><ExitIcon /></MenuIcon>
                    {t('investors.userMenu.logout')}
                  </MenuRow>
                </DesktopDropdown>
              ) : null}
            </div>
          )}
        </Downshift>
      </ShowAt>
      <ShowAt to={2}>
        <MobileMenuButton data-auto="investors_home_v2_header_mobile_menu_button" onClick={() => setIsOpen(true)}>
          <UserIcon />
        </MobileMenuButton>
        {/* bottom-sheet on mobile, per explicit request — reuses the shared Modal's own
            mobileModalPositionBottom prop instead of a bespoke full-screen overlay. Conditionally
            mounted (not rendered unconditionally with isOpen as a prop) — Modal always mounts
            OverlayWrapper regardless of isOpen, which locks page scroll as soon as it's in the
            tree; every other Modal consumer in this codebase mounts it conditionally for the same
            reason. hideCloseButton + a custom MobileMenuHeader/MobileMenuCloseButton replace the
            shared Modal's own close button, which doesn't match Figma's design here (see
            AvatarMenu.styled.ts). */}
        {isOpen ? (
          <Modal
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            mobileModalPositionBottom
            hideCloseButton
            contentWrapperStyle={{ padding: 0, borderRadius: '16px 16px 0 0' }}
          >
            <MobileMenuHeader>
              <MobileMenuCloseButton data-auto="investors_home_v2_header_mobile_menu_close" onClick={() => setIsOpen(false)}>
                <CloseIcon />
              </MobileMenuCloseButton>
            </MobileMenuHeader>
            <MobileMenuList data-auto="investors_home_v2_header_mobile_menu_page">
              <MobileMenuRow data-auto="investors_home_v2_header_menu_my_account" onClick={goTo(Route.InvestorsProfilePage)}>
                <MenuIcon iconWidth={14}><ProfileIcon /></MenuIcon>
                {t('investors.home.v2.header.menu.myAccount')}
              </MobileMenuRow>
              <MobileMenuRow
                data-auto="investors_home_v2_header_menu_investment_profile"
                onClick={goTo(Route.InvestorsProfilePage, { initialTab: ProfileTab.Preferences })}
              >
                <MenuIcon iconWidth={16}><DocumentIcon /></MenuIcon>
                {t('investors.home.v2.header.menu.investmentProfile')}
              </MobileMenuRow>
              <MobileMenuRow data-auto="investors_home_v2_header_menu_saves" onClick={goTo(Route.InvestorsFavoriteNeighourhoodPage)}>
                <MenuIcon iconWidth={14}><BookmarkIcon /></MenuIcon>
                {t('investors.home.v2.header.menu.saves')}
              </MobileMenuRow>
              <MobileMenuRow data-auto="investors_home_v2_header_menu_logout" onClick={handleLogout}>
                <MenuIcon iconWidth={20}><ExitIcon /></MenuIcon>
                {t('investors.userMenu.logout')}
              </MobileMenuRow>
            </MobileMenuList>
          </Modal>
        ) : null}
      </ShowAt>
    </MenuContainer>
  );
};

const mapStateToProps = (state: State) => ({
  userProfile: userProfileFieldsSelector(state),
});

const mapDispatchToProps = {
  navigate: navigateTo,
  onLogout: logout,
};

export const AvatarMenu = connect(mapStateToProps, mapDispatchToProps)(AvatarMenuBase);
